import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import styles from './Login.module.css';
import routes from 'routes';

import { login } from 'api/authApi';
import {
  persistSession,
  consumeSessionExpiredFlag,
} from 'services/auth/tokenService';

import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import CustomCheckbox from 'components/common/CustomCheckbox';
import MetaTags from 'components/common/MetaTags';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formValues, setFormValues] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});

  // The api layer lands the user here (full page load) when the server rejects their token mid-session — tell them
  // why they were logged out. The flag survives the reload in sessionStorage; consuming it makes the message one-shot.
  useEffect(() => {
    if (consumeSessionExpiredFlag()) {
      toast.error('Your login session has expired. Please log in again.');
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formValues.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      newErrors.email = 'Email address is invalid';
    }
    if (!formValues.password) {
      newErrors.password = 'Password is required';
    }

    return newErrors;
  };

  const clickLoginButton = async () => {
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      try {
        const response = await login(formValues);
        // One shared helper persists the session (clears both storages first, then writes to one) — same code path as
        // the public registration dialog, so they can't drift. "Remember me" is the only persistence opt-in.
        persistSession(response.data, { remember: rememberMe, email: formValues.email });
        toast.success('Login successful.');
        // Honor a safe internal ?redirect= (e.g. the public meet landing page sends the user here and back). Only
        // same-origin relative paths are allowed, never a protocol-relative "//host" open-redirect.
        const redirectTo = searchParams.get('redirect');
        const safeRedirect =
          redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//') ? redirectTo : null;
        // A user with no active club is a platform citizen → their personal portal.
        const clubPermission = response?.data?.clubPermissions?.[0] ?? null;
        navigate(safeRedirect || (clubPermission ? routes.admin.dashboard : routes.personal.home));
      } catch (e) {
        console.log('error: ', e);
        toast.error(e.response?.data || 'Login Failed');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <MetaTags title="SplitFast | Login" />
      <div className={styles.loginContainer}>
        <div className={styles.headerSection}>
          <h1 className="heading1">Login to your Athletics Club</h1>
          <p className={styles.subtitle}>
            Enter your credentials to access your club calendar and results.
          </p>
        </div>

        <div className={styles.inputSection}>
          <CustomInput
            label="Email Address"
            type="text"
            name="email"
            placeholder="myemail@gmail.com"
            error={errors.email}
            onChange={handleInputChange}
            style={{ marginBottom: 20 }}
            inputStyle={{
              fontSize: '15px',
              height: '60px',
            }}
          />
          <CustomInput
            label="Password"
            type="password"
            name="password"
            placeholder="Type password"
            error={errors.password}
            onChange={handleInputChange}
            style={{ marginBottom: 20 }}
            inputStyle={{
              fontSize: '15px',
              height: '60px',
            }}
          />
        </div>

        <div className={styles.optionsSection}>
          <CustomCheckbox
            label="Remember Me"
            onChange={() => setRememberMe(!rememberMe)}
            checked={rememberMe}
            style={{ margin: 0 }}
            labelStyle={{ fontWeight: 'normal' }}
          />
          <Link
            to={routes.auth.forgotPassword}
            className={styles.forgotPassword}
          >
            Forgot Password?
          </Link>
        </div>

        <div className={styles.buttonSection}>
          <CustomButton
            disabled={loading}
            onClick={clickLoginButton}
            style={{
              ...defaultButtonStyle,
              width: '100%',
              maxWidth: 400,
            }}
          >
            {loading ? 'Logging in...' : 'Log in'}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default Login;
